import React, { useCallback, useEffect, useState } from 'react';
import { showComponentModal } from '../../../utils/showUploadPhotoModal';
import { BASE_URL, UPLOAD_ROOT_PATH } from '../../../utils/Constants';
import { CreateAccountService, GetStatusService } from '../../../services/BiostarEnrollmentService';
import PhotoEnrollmentDialog from './PhotoEnrollmentDialog';
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Hard ceiling matching the API routes - a photo over MAX_FILE_SIZE_BYTES but under this
 * still uploads, so the checklist can flag its size against the photo the student picked.
 */
export const MAX_UPLOAD_SIZE_BYTES = 25 * 1024 * 1024;

/**
 * The `code` values the Biostar enrollment endpoints return (see the server's
 * BiostarEnrollmentManager). Anything not listed here is a raw Biostar validation
 * code, surfaced as-is along with its message.
 */
export const PHOTO_RESULT_CODES = {
    /** '0' = Biostar accepted the photo; every other Biostar code is a rejection. */
    BIOSTAR_SUCCESS: '0',

    /** Not a JPEG or PNG, per the server's format check (mirrored by runLocalChecks). */
    LOCAL_INVALID_FORMAT: 'LOCAL_INVALID_FORMAT',

    /** Over the server's 10MB cap (mirrored by runLocalChecks). */
    LOCAL_FILE_TOO_LARGE: 'LOCAL_FILE_TOO_LARGE',

    /** submitPhoto only: the uploaded photo is gone, so there's nothing left to enroll. */
    NO_TEMP_PHOTO: 'NO_TEMP_PHOTO',

    /** Mobile relay: the 'photo-received' ack, sent before the photo is processed. */
    UPLOAD_RECEIVED: 'UPLOAD_RECEIVED',

    /** Mobile relay: the phone's own response once the photo reached the desktop session. */
    UPLOAD_SUCCESS: 'UPLOAD_SUCCESS',
};

/**
 * Screens of the enrollment dialog, in the order they're reached. Every stage-driven
 * branch in the dialog and its child components compares against these.
 */
export const PHOTO_STAGES = {
    /** Landing screen: requirements, QR code and the upload/capture buttons. */
    OPTIONS: 'options',

    /** Live webcam feed, waiting for a capture. */
    WEBCAM: 'webcam',

    /** A photo is on its way to the server for validation. */
    UPLOADING: 'uploading',

    /** The validated photo is on screen with its checklist, awaiting Submit. */
    PREVIEW: 'preview',

    /** Submit was pressed; enrollment is in flight. */
    SUBMITTING: 'submitting',

    /** Enrolled - the confirmation screen. */
    SUCCESS: 'success',
};

/**
 * The stages that render PhotoCapturePreview rather than the options or success screen.
 */
export const CAPTURE_STAGES = [
    PHOTO_STAGES.WEBCAM,
    PHOTO_STAGES.UPLOADING,
    PHOTO_STAGES.PREVIEW,
    PHOTO_STAGES.SUBMITTING,
];

/**
 * Mirrors the backend's local format/size checks (JPEG/PNG, max 10MB) so the
 * student gets instant feedback before anything is sent to Biostar.
 */
export const runLocalChecks = (type, sizeBytes) => {
    return {
        format: ALLOWED_IMAGE_TYPES.includes(type),
        size: sizeBytes > 0 && sizeBytes <= MAX_FILE_SIZE_BYTES,
    };
};

/**
 * Draws the current frame of a video element onto a canvas, and resolves with
 * it as a JPEG Blob ready to upload.
 */
export const captureFrameFromVideo = (videoEl, canvasEl) => {
    canvasEl.width = videoEl.videoWidth;
    canvasEl.height = videoEl.videoHeight;
    canvasEl.getContext('2d').drawImage(videoEl, 0, 0);
    return new Promise((resolve) => canvasEl.toBlob(resolve, 'image/jpeg', 0.92));
};

/**
 * True when running on localhost or staging2 - neither can be reliably scanned
 * by a real phone (localhost isn't reachable from a phone; staging2 is a debug
 * environment), so the QR code needs to point elsewhere on localhost (see
 * buildMobileUploadUrl) and gets a clickable debug shortcut on both.
 */
export const isDebugOrigin = () =>
    window.location.origin.includes('localhost') || window.location.origin.includes('staging2');

/**
 * Builds the mobileUpload URL that gets encoded into the QR code for a given
 * securityToken (from startMobileUploadSession) - the mobile page is
 * unauthenticated, so this signed token is the only thing it carries; the
 * server recovers studentId from it rather than trusting the URL.
 */
export const buildMobileUploadUrl = (securityToken) => {
    const appUrl = isDebugOrigin()
        ? 'https://staging2.student.myaie.ac'
        : process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    return `${appUrl}/mobileUpload/${securityToken}`;
};

/**
 * Builds the real (non-staging-swapped) mobileUpload URL for a given
 * securityToken - only meaningful on a debug origin, where it's used as a
 * shortcut so the QR code can be clicked directly instead of scanned from a phone.
 */
export const buildLocalMobileUploadUrl = (securityToken) => `${window.location.origin}/mobileUpload/${securityToken}`;

/**
 * Turns an absolute server-side file path (as returned by uploadPhoto/
 * relayPhotoFromMobile) into a viewable URL, using the same
 * BASE_URL/UPLOAD_ROOT_PATH convention already used elsewhere in the app
 * (e.g. front/src/pages/Library/CampusLibraries.js) for server-stored files.
 */
export const buildTempPhotoUrl = (photoPath) => {
    if (!photoPath) {
        return null;
    }

    // photoPath is a stable per-student filename (e.g. relayTemp/<studentId>.jpeg), so the URL
    // is identical across retakes - append a cache-buster or the browser reuses the old image.
    return `${BASE_URL}/${photoPath.replace(`${UPLOAD_ROOT_PATH}/`, '')}?t=${Date.now()}`;
};

/**
 * Stops all tracks on a MediaStream, if one is provided.
 */
export const stopMediaStream = (stream) => {
    if (stream) {
        stream.getTracks().forEach((track) => track.stop());
    }
};

/**
 * Checks whether the student has a Biostar account and creates one if not, so it's
 * ready by the time they reach Submit (enrollPhoto needs an existing Biostar user).
 * Runs once on mount; call `retry` after a retake if it hadn't succeeded yet.
 */
export function useBiostarAccount() {
    const [isAccountReady, setIsAccountReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const init = useCallback(async () => {
        let step = 'FETCHING_STATUS';
        setIsLoading(true);
        // Cleared up front, so a successful retry doesn't leave the previous failure
        // on screen next to an account that is now ready.
        setErrorMessage('');
        try {
            const response = await GetStatusService();
            const status = response?.data?.status;

            if (status === 'Not Created') {
                step = 'CREATING_ACCOUNT';
                await CreateAccountService();
            }
            setIsAccountReady(true);
        } catch (err) {
            console.error('useBiostarAccount->init->Error', err);
            setErrorMessage(
                `${step === 'FETCHING_STATUS' ? "Couldn't fetch account status" : "Couldn't set up account"}. Please try again.`,
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        init();
    }, [init]);

    return { isAccountReady, isLoading, errorMessage, retry: init };
}

/**
 * The full dialog width.
 */
const dialogWidth = 820;

/**
 * Opens the Biostar photo enrollment flow inside a SweetAlert2 popover.
 * Call this from a button's onClick instead of managing modal open/close state.
 * @param {Function} onPhotoUpdated Called once the new photo is enrolled and saved
 * @param {boolean} hasPhoto Whether the student already has a profile photo
 */
export function showPhotoEnrollmentDialog({ onPhotoUpdated, hasPhoto } = {}) {
    showComponentModal(
        <PhotoEnrollmentDialog
            onPhotoUpdated={(body) => {
                if (onPhotoUpdated) {
                    onPhotoUpdated(body);
                }
            }}
            hasPhoto={hasPhoto}
        />,
        { width: dialogWidth },
    );
}
